import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSystem } from '@ohif/core';
import type { Facility, FacilityType } from '../../types/facility.types';
import facilityService from '../../services/facilityService';
import authService from '../../auth/authService';
import locationService from '../../services/locationService';
import { mapApiResponseToFormValues, type FacilityFormValues } from '../../utils/facilityMapper';
import { Button, cn, Icons } from '@ohif/ui-next';
import { VirtualizedDropdown, type VirtualizedDropdownOption } from '../../components/common';
import { validateFacilityDetailsViewDraft } from '../../validation/facilityFormValidation';
import './FacilityDetailsView.css';

/** API may return fields not yet on the Facility type */
type FacilityDetailsApi = Facility & {
  dual_stage_report_approval?: string | null;
  senior_radiologist_review_required?: string | null;
};

const FACILITY_TYPES: FacilityType[] = ['Hospital', 'Diagnostic Center', 'Clinic', 'HUB', 'SPOKE'];

/** Country list — matches Add Facility (India-only) */
const COUNTRY_OPTIONS: { value: string; label: string }[] = [{ value: 'India', label: 'India' }];

/** Match API / free-text location to dropdown option value (slug), same idea as Add Facility */
function resolveLocationOptionValue(
  raw: string | undefined | null,
  options: { value: string; label: string }[]
): string {
  if (raw == null || options.length === 0) {
    return '';
  }
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === '-') {
    return '';
  }
  if (options.some(o => o.value === trimmed)) {
    return trimmed;
  }
  const lower = trimmed.toLowerCase();
  const byLabel = options.find(o => o.label.toLowerCase() === lower);
  if (byLabel) {
    return byLabel.value;
  }
  const slug = lower.replace(/\s+/g, '-');
  const bySlug = options.find(o => o.value === slug);
  if (bySlug) {
    return bySlug.value;
  }
  return trimmed;
}

function locationLabelForRead(
  raw: string | undefined | null,
  options: { value: string; label: string }[]
): string {
  if (raw == null || String(raw).trim() === '' || String(raw) === '-') {
    return '-';
  }
  const resolved = resolveLocationOptionValue(String(raw), options);
  const opt = options.find(o => o.value === resolved);
  return opt?.label ?? String(raw).trim();
}
// const INTEGRATION_STATUSES: IntegrationStatus[] = ['Connected', 'Pending', 'Failed']; // with PACS/RIS tab
const SENIOR_REVIEW_OPTIONS = [
  { value: 'Senior Radiologist', label: 'Senior Radiologist' },
  { value: 'QC Technician', label: 'QC Technician' },
] as const;

function disp(v: unknown): string {
  if (v === null || v === undefined) {
    return '-';
  }
  if (typeof v === 'string' && v.trim() === '') {
    return '-';
  }
  return String(v);
}

function facilityToFormValues(f: FacilityDetailsApi): FacilityFormValues {
  const p = mapApiResponseToFormValues(f);
  const senior =
    f.senior_radiologist_review_required != null &&
    String(f.senior_radiologist_review_required).trim() !== ''
      ? String(f.senior_radiologist_review_required).trim()
      : 'Senior Radiologist';

  return {
    name: p.name ?? '',
    code: p.code ?? '',
    type: (p.type as FacilityType) || 'Hospital',
    description: p.description ?? '',
    phoneNo: (p.phoneNo ?? '').replace(/\D/g, '').slice(0, 10),
    emailId: p.emailId ?? '',
    addressLine1: p.addressLine1 ?? '',
    addressLine2: p.addressLine2 ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    pincode: p.pincode ?? '',
    country: p.country ?? '',
    logo: null,
    logoFileName: p.logoFileName ?? '',
    headerText: p.headerText ?? '',
    footerText: p.footerText ?? '',
    pacsAETitle: p.pacsAETitle ?? '',
    pacsIPAddress: p.pacsIPAddress ?? '',
    pacsPort: p.pacsPort ?? '',
    integrationStatus: p.integrationStatus ?? 'Pending',
    status: p.status ?? 'Active',
    employeeId: p.employeeId ?? '',
    fullName: p.fullName ?? '',
    adminPhoneNo: p.adminPhoneNo ?? '',
    adminEmailId: p.adminEmailId ?? '',
    gender: p.gender ?? '',
    username: p.username ?? '',
    password: p.password ?? '',
    designation: p.designation ?? '',
    hasRadiologistHierarchy: p.hasRadiologistHierarchy ?? 'No',
    risUrl: f.ris_url ?? '',
    letterheadLogoUrl: f.letterhead_logo ?? '',
    seniorRadiologistReviewRequired: senior,
  };
}

type ActiveTab = 'basic' | 'pacs' | 'letterhead';

function FieldLabel({
  children,
  required,
}: Readonly<{
  children: React.ReactNode;
  required?: boolean;
}>) {
  return (
    <div className="flex w-full shrink-0 items-center gap-[3px]">
      <span className="facility-details-field-label text-[14px] leading-[14px] text-[#0a0a0a]">
        {children}
      </span>
      {required && <span className="text-[14px] leading-[14px] text-[#fb2c36]">*</span>}
    </div>
  );
}

function HelperText({ children }: Readonly<{ children: React.ReactNode }>) {
  return <p className="mt-0 text-[12px] leading-[16px] text-[#737373]">{children}</p>;
}

function FieldGroup({
  label,
  required,
  helper,
  errorMessage,
  children,
}: {
  label: string;
  required?: boolean;
  helper?: string;
  errorMessage?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel required={required}>{label}</FieldLabel>
      {children}
      {errorMessage ? (
        <p className="m-0 text-[12px] leading-[16px] text-[#dc2626]">{errorMessage}</p>
      ) : (
        helper && <HelperText>{helper}</HelperText>
      )}
    </div>
  );
}

function ReadInput({
  value,
  type = 'text',
  id,
}: Readonly<{
  value: string;
  type?: string;
  id?: string;
}>) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      readOnly
      tabIndex={-1}
      className={cn(
        'h-9 w-full cursor-default rounded-[5.2px] border border-[#e5e5e5] bg-[#f3f3f5] px-5 py-1',
        'text-[14px] leading-[20px] text-[#0a0a0a]',
        'focus:outline-none focus:ring-0'
      )}
    />
  );
}

function ReadTextarea({
  value,
  id,
  rows = 4,
}: Readonly<{ value: string; id?: string; rows?: number }>) {
  return (
    <textarea
      id={id}
      value={value}
      readOnly
      tabIndex={-1}
      rows={rows}
      className={cn(
        'w-full resize-none rounded-[5.2px] border border-[#e5e5e5] bg-[#f3f3f5] px-5 py-2',
        'text-[14px] leading-[20px] text-[#0a0a0a]',
        'cursor-default focus:outline-none focus:ring-0'
      )}
    />
  );
}

const editableInputClass = cn(
  'h-9 w-full rounded-[5.2px] border border-[#e5e5e5] bg-white px-5 py-1',
  'text-[14px] leading-[20px] text-[#0a0a0a]',
  'focus:outline-none focus:ring-1 focus:ring-[#0f91b2]'
);

const editableTextareaClass = cn(
  'w-full resize-none rounded-[5.2px] border border-[#e5e5e5] bg-white px-5 py-2',
  'text-[14px] leading-[20px] text-[#0a0a0a]',
  'focus:outline-none focus:ring-1 focus:ring-[#0f91b2]'
);

function EditableInput({
  value,
  onChange,
  isEditing,
  type = 'text',
  id,
  invalid = false,
  maxLength,
  inputMode,
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  isEditing: boolean;
  type?: string;
  id?: string;
  invalid?: boolean;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
}) {
  if (!isEditing) {
    return (
      <ReadInput
        id={id}
        type={type}
        value={value}
      />
    );
  }
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      aria-invalid={invalid}
      maxLength={maxLength}
      inputMode={inputMode}
      autoComplete={autoComplete}
      className={cn(editableInputClass, invalid && 'border-[#dc2626] focus:ring-[#dc2626]')}
    />
  );
}

function EditableTextarea({
  value,
  onChange,
  isEditing,
  id,
  rows = 4,
  invalid = false,
  maxLength,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  isEditing: boolean;
  id?: string;
  rows?: number;
  invalid?: boolean;
  maxLength?: number;
}>) {
  if (!isEditing) {
    return (
      <ReadTextarea
        id={id}
        value={value}
        rows={rows}
      />
    );
  }
  return (
    <textarea
      id={id}
      value={value}
      rows={rows}
      maxLength={maxLength}
      aria-invalid={invalid}
      onChange={e => onChange(e.target.value)}
      className={cn(editableTextareaClass, invalid && 'border-[#dc2626] focus:ring-[#dc2626]')}
    />
  );
}

function EditableSelect({
  value,
  onChange,
  isEditing,
  options,
  id,
  displayRead,
  disabled = false,
  invalid = false,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  isEditing: boolean;
  options: { value: string; label: string }[];
  id?: string;
  displayRead?: (v: string) => string;
  disabled?: boolean;
  invalid?: boolean;
}>) {
  const vdOptions: VirtualizedDropdownOption[] = useMemo(
    () => options.map((o, i) => ({ id: i + 1, label: o.label })),
    [options]
  );

  const selectedId = useMemo((): number | null => {
    const idx = options.findIndex(o => o.value === value);
    return idx >= 0 ? idx + 1 : null;
  }, [value, options]);

  const handleDropdownChange = (numericId: number | null, _label: string) => {
    if (numericId === null) {
      return;
    }
    const opt = options[numericId - 1];
    if (opt) {
      onChange(opt.value);
    }
  };

  if (!isEditing) {
    const shown = displayRead
      ? displayRead(value)
      : (options.find(o => o.value === value)?.label ?? value);
    return (
      <div
        id={id}
        className="w-full min-w-0"
      >
        <div
          className="facility-details-readonly-select-trigger pointer-events-none flex h-9 w-full shrink-0 cursor-default select-none items-center justify-between rounded-[5.2px] border border-[#e5e5e5] bg-[#f3f3f5] px-[21px] py-px text-left text-[14px] leading-5"
          aria-disabled="true"
        >
          <span className="truncate text-[#0a0a0a]">{shown}</span>
          <Icons.ChevronDown
            className="h-4 w-4 shrink-0 text-[#737373]"
            aria-hidden
          />
        </div>
      </div>
    );
  }

  return (
    <div
      id={id}
      className="w-full min-w-0"
    >
      <VirtualizedDropdown
        value={selectedId}
        onChange={handleDropdownChange}
        options={vdOptions}
        placeholder="Select..."
        showNone={false}
        noneLabel=""
        showSearch={options.length > 6}
        searchPlaceholder="Search..."
        className={cn(
          '!focus:ring-[#cbd5e1] w-full',
          invalid && '!focus:ring-[#dc2626] !border-[#dc2626]'
        )}
        containerClassName="w-full min-w-0"
        variant="light"
        disabled={disabled}
      />
    </div>
  );
}

function SectionCard({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <div className="flex w-full min-w-0 flex-col rounded-[11.2px] border border-[#e5e5e5] bg-white">
      <div className="facility-details-section-head">
        <h2 className="facility-details-section-title text-[16px] leading-[16px] text-[#0a0a0a]">
          {title}
        </h2>
      </div>
      <div className="mx-0 border-b border-[#e5e5e5]" />
      <div className="facility-details-section-body">{children}</div>
    </div>
  );
}

function statusPillClass(status: Facility['status']): string {
  if (status === 'Active') {
    return 'bg-[rgba(0,166,62,0.2)] text-[#008236] border border-[rgba(0,201,80,0.3)]';
  }
  if (status === 'Inactive') {
    return 'border border-gray-200 bg-gray-100 text-gray-600';
  }
  return 'border border-red-200 bg-red-100 text-red-700';
}

/* PACS/RIS tab — IntegrationBadge (uncomment with tab block)
function IntegrationBadge({ status }: { status: Facility['integration_status'] | undefined }) {
  const iconStroke = 2;
  if (status === 'Connected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-green-500/30 bg-green-600/20 px-2 py-0.5 text-[12px] font-medium text-green-700">
        <svg
          width={12}
          height={12}
          className="shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={iconStroke}
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        Connected
      </span>
    );
  }
  if (status === 'Pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-600/20 px-2 py-0.5 text-[12px] font-medium text-amber-700">
        <svg
          width={12}
          height={12}
          className="shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={iconStroke}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        Pending
      </span>
    );
  }
  if (status === 'Failed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-600/20 px-2 py-0.5 text-[12px] font-medium text-red-700">
        <svg
          width={12}
          height={12}
          className="shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={iconStroke}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-gray-500/30 bg-gray-600/20 px-2 py-0.5 text-[12px] font-medium text-gray-700">
      Not configured
    </span>
  );
}
*/

const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mr-1.5"
    aria-hidden
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

function FacilityDetailsBasicInfoSection(
  props: Readonly<{
    isEditing: boolean;
    draft: FacilityFormValues;
    data: FacilityDetailsApi;
    fieldErrors: Partial<Record<string, string>>;
    setField: <K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) => void;
    facilityTypeOptions: { value: string; label: string }[];
    statusLabel: string;
  }>
) {
  const { isEditing, draft, data, fieldErrors, setField, facilityTypeOptions, statusLabel } = props;
  return (
    <SectionCard title="Basic Information">
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
        <FieldGroup
          label="Facility Name"
          required
          errorMessage={isEditing ? fieldErrors.name : undefined}
        >
          <EditableInput
            id="fd-name"
            isEditing={isEditing}
            value={isEditing ? draft.name : disp(data.facility_name)}
            onChange={v => setField('name', v)}
            invalid={isEditing && !!fieldErrors.name}
          />
        </FieldGroup>
        <FieldGroup
          label="Facility Code"
          helper="Code cannot be changed"
        >
          <ReadInput
            id="fd-code"
            value={disp(data.facility_code)}
          />
        </FieldGroup>
        <FieldGroup
          label="Facility Type"
          required
          errorMessage={isEditing ? fieldErrors.type : undefined}
        >
          <EditableSelect
            id="fd-type"
            isEditing={isEditing}
            value={isEditing ? draft.type : String(data.facility_type)}
            onChange={v => setField('type', v)}
            options={facilityTypeOptions}
            invalid={isEditing && !!fieldErrors.type}
          />
        </FieldGroup>
        <FieldGroup
          label="Status"
          helper="Status is managed by Super Admin"
        >
          <ReadInput
            id="fd-status"
            value={statusLabel}
          />
        </FieldGroup>
        <div className="md:col-span-2">
          <FieldGroup
            label="Facility Description"
            errorMessage={isEditing ? fieldErrors.description : undefined}
          >
            <EditableTextarea
              id="fd-desc"
              isEditing={isEditing}
              value={isEditing ? draft.description : disp(data.facility_description)}
              onChange={v => setField('description', v)}
              rows={4}
              maxLength={250}
              invalid={isEditing && !!fieldErrors.description}
            />
          </FieldGroup>
        </div>
      </div>
    </SectionCard>
  );
}

function FacilityDetailsAddressSection(
  props: Readonly<{
    isEditing: boolean;
    draft: FacilityFormValues;
    data: FacilityDetailsApi;
    fieldErrors: Partial<Record<string, string>>;
    setField: <K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) => void;
    stateOptions: { value: string; label: string }[];
    cityOptions: { value: string; label: string }[];
    isLoadingStates: boolean;
    isLoadingCities: boolean;
    stateForCityFetch: string;
  }>
) {
  const {
    isEditing,
    draft,
    data,
    fieldErrors,
    setField,
    stateOptions,
    cityOptions,
    isLoadingStates,
    isLoadingCities,
    stateForCityFetch,
  } = props;
  return (
    <SectionCard title="Address & Contact Information">
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <FieldGroup
            label="Address Line 1"
            required
            errorMessage={isEditing ? fieldErrors.addressLine1 : undefined}
          >
            <EditableInput
              id="fd-a1"
              isEditing={isEditing}
              value={isEditing ? draft.addressLine1 : disp(data.address_line_1)}
              onChange={v => setField('addressLine1', v)}
              invalid={isEditing && !!fieldErrors.addressLine1}
            />
          </FieldGroup>
        </div>
        <div className="md:col-span-2">
          <FieldGroup
            label="Address Line 2"
            errorMessage={isEditing ? fieldErrors.addressLine2 : undefined}
          >
            <EditableInput
              id="fd-a2"
              isEditing={isEditing}
              value={isEditing ? draft.addressLine2 : disp(data.address_line_2)}
              onChange={v => setField('addressLine2', v)}
              invalid={isEditing && !!fieldErrors.addressLine2}
            />
          </FieldGroup>
        </div>
        <FieldGroup
          label="State"
          required
          errorMessage={isEditing ? fieldErrors.state : undefined}
        >
          <EditableSelect
            id="fd-state"
            isEditing={isEditing}
            value={resolveLocationOptionValue(
              isEditing ? draft.state : String(data.state ?? ''),
              stateOptions
            )}
            onChange={v => {
              setField('state', v);
              setField('city', '');
            }}
            options={stateOptions}
            disabled={isLoadingStates}
            displayRead={v => locationLabelForRead(v, stateOptions)}
            invalid={isEditing && !!fieldErrors.state}
          />
        </FieldGroup>
        <FieldGroup
          label="City"
          required
          errorMessage={isEditing ? fieldErrors.city : undefined}
        >
          <EditableSelect
            id="fd-city"
            isEditing={isEditing}
            value={resolveLocationOptionValue(
              isEditing ? draft.city : String(data.city ?? ''),
              cityOptions
            )}
            onChange={v => setField('city', v)}
            options={cityOptions}
            disabled={isLoadingCities || !stateForCityFetch}
            displayRead={v => locationLabelForRead(v, cityOptions)}
            invalid={isEditing && !!fieldErrors.city}
          />
        </FieldGroup>
        <FieldGroup
          label="Country"
          required
        >
          <EditableSelect
            id="fd-country"
            isEditing={isEditing}
            value={resolveLocationOptionValue(
              (() => {
                const raw = isEditing ? draft.country : String(data.country ?? '');
                const t = raw.trim();
                return t === '' ? 'India' : raw;
              })(),
              COUNTRY_OPTIONS
            )}
            onChange={v => setField('country', v)}
            options={COUNTRY_OPTIONS}
            displayRead={v => locationLabelForRead(v, COUNTRY_OPTIONS)}
          />
        </FieldGroup>
        <FieldGroup
          label="Pincode"
          required
          errorMessage={isEditing ? fieldErrors.pincode : undefined}
        >
          <EditableInput
            id="fd-pin"
            isEditing={isEditing}
            value={isEditing ? draft.pincode : disp(data.pincode)}
            onChange={v => setField('pincode', v.replaceAll(/\D/g, '').slice(0, 6))}
            invalid={isEditing && !!fieldErrors.pincode}
            maxLength={6}
            inputMode="numeric"
            autoComplete="postal-code"
          />
        </FieldGroup>
        <FieldGroup
          label="Contact Number"
          required
          errorMessage={isEditing ? fieldErrors.phoneNo : undefined}
        >
          <EditableInput
            id="fd-phone"
            isEditing={isEditing}
            value={isEditing ? draft.phoneNo : disp(data.contact_number)}
            onChange={v => setField('phoneNo', v.replaceAll(/\D/g, '').slice(0, 10))}
            invalid={isEditing && !!fieldErrors.phoneNo}
            maxLength={10}
            inputMode="numeric"
            autoComplete="tel"
          />
        </FieldGroup>
        <FieldGroup
          label="Email"
          required
          errorMessage={isEditing ? fieldErrors.emailId : undefined}
        >
          <EditableInput
            id="fd-email"
            type="email"
            isEditing={isEditing}
            value={isEditing ? draft.emailId : disp(data.email_id)}
            onChange={v => setField('emailId', v)}
            invalid={isEditing && !!fieldErrors.emailId}
          />
        </FieldGroup>
      </div>
    </SectionCard>
  );
}

function FacilityDetailsReportApprovalSection(
  props: Readonly<{
    isEditing: boolean;
    draft: FacilityFormValues;
    fieldErrors: Partial<Record<string, string>>;
    setField: <K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) => void;
    hierarchyOptions: { value: string; label: string }[];
    dualStageLabel: string;
    seniorLabel: string;
  }>
) {
  const { isEditing, draft, fieldErrors, setField, hierarchyOptions, dualStageLabel, seniorLabel } =
    props;
  return (
    <SectionCard title="Report Approval Configuration">
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
        <FieldGroup
          label="Dual Stage Report Approval"
          errorMessage={isEditing ? fieldErrors.hasRadiologistHierarchy : undefined}
        >
          {isEditing ? (
            <EditableSelect
              id="fd-dual"
              isEditing
              value={draft.hasRadiologistHierarchy ?? 'No'}
              onChange={v => setField('hasRadiologistHierarchy', v)}
              options={hierarchyOptions}
              invalid={!!fieldErrors.hasRadiologistHierarchy}
            />
          ) : (
            <ReadInput
              id="fd-dual"
              value={dualStageLabel}
            />
          )}
        </FieldGroup>
        <FieldGroup
          label="Approved By "
          helper="Select whether reports should be verified by QC Technician or Senior Radiologist"
        >
          {isEditing ? (
            <EditableSelect
              id="fd-senior"
              isEditing
              value={
                SENIOR_REVIEW_OPTIONS.some(o => o.value === draft.seniorRadiologistReviewRequired)
                  ? draft.seniorRadiologistReviewRequired!
                  : 'Senior Radiologist'
              }
              onChange={v => setField('seniorRadiologistReviewRequired', v)}
              options={[...SENIOR_REVIEW_OPTIONS]}
            />
          ) : (
            <ReadInput
              id="fd-senior"
              value={seniorLabel}
            />
          )}
        </FieldGroup>
      </div>
    </SectionCard>
  );
}

function FacilityDetailsHeaderActions({
  isEditing,
  saving,
  draftValid,
  onCancel,
  onSave,
  onEdit,
}: Readonly<{
  isEditing: boolean;
  saving: boolean;
  draftValid: boolean;
  onCancel: () => void;
  onSave: () => void;
  onEdit: () => void;
}>) {
  if (isEditing) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-9 rounded-[5.2px] border border-[#e5e5e5] bg-white px-4 text-[14px] text-[#0a0a0a] hover:bg-[#f3f3f5]"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={saving || !draftValid}
          className="h-9 rounded-[5.2px] border-0 bg-[#0f91b2] px-4 text-[14px] text-[#eafdfe] hover:bg-[#0d7d9a] disabled:opacity-60"
          title={draftValid ? undefined : 'Complete all required fields to save'}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        type="button"
        onClick={onEdit}
        className="h-9 shrink-0 rounded-[5.2px] border-0 bg-[#0f91b2] px-4 text-[14px] text-[#eafdfe] hover:bg-[#0d7d9a]"
      >
        <EditIcon />
        Edit Details
      </Button>
    </div>
  );
}

export interface FacilityDetailsViewProps {
  facilityData: FacilityDetailsApi;
  onUpdateSuccess?: (updated: Facility) => void;
}

const FacilityDetailsView: React.FC<FacilityDetailsViewProps> = ({
  facilityData,
  onUpdateSuccess,
}) => {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const data = facilityData;
  const [activeTab, setActiveTab] = useState<ActiveTab>('basic');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<FacilityFormValues>(() => facilityToFormValues(facilityData));

  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraft(facilityToFormValues(facilityData));
    }
  }, [facilityData, isEditing]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingStates(true);
    locationService
      .getStates()
      .then(states => {
        if (!cancelled) {
          setStateOptions(states);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStateOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingStates(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stateForCityFetch = useMemo(() => {
    if (isEditing) {
      return draft.state?.trim() ?? '';
    }
    const s = data.state;
    if (s == null) {
      return '';
    }
    const str = String(s).trim();
    return str === '-' ? '' : str;
  }, [isEditing, draft.state, data.state]);

  useEffect(() => {
    if (!stateForCityFetch) {
      setCityOptions([]);
      setIsLoadingCities(false);
      return;
    }
    let cancelled = false;
    setIsLoadingCities(true);
    locationService
      .getCities(stateForCityFetch)
      .then(cities => {
        if (!cancelled) {
          setCityOptions(cities);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCityOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingCities(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stateForCityFetch]);

  const setField = useCallback(
    <K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) => {
      setDraft(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const draftValidation = useMemo(() => validateFacilityDetailsViewDraft(draft), [draft]);
  const fieldErrors = draftValidation.fields;

  const dualStageLabel = useMemo(() => {
    if (
      data.dual_stage_report_approval != null &&
      String(data.dual_stage_report_approval).trim() !== ''
    ) {
      return disp(data.dual_stage_report_approval);
    }
    if (data.has_radiologist_hierarchy === true) {
      return 'Enabled';
    }
    if (data.has_radiologist_hierarchy === false) {
      return 'Disabled';
    }
    return 'Enabled';
  }, [data]);

  const seniorLabel = useMemo(() => {
    if (
      data.senior_radiologist_review_required != null &&
      String(data.senior_radiologist_review_required).trim() !== ''
    ) {
      return disp(data.senior_radiologist_review_required);
    }
    return 'Senior Radiologist';
  }, [data]);

  const statusLabel = data.status != null ? String(data.status) : '-';

  /* Letterhead tab — logo preview (uncomment with tab block)
  const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!draft.logo) {
      setLogoObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(draft.logo);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draft.logo]);

  const previewLogoSrc = useMemo(() => {
    if (logoObjectUrl) {
      return logoObjectUrl;
    }
    const url = (isEditing ? draft.letterheadLogoUrl : data.letterhead_logo)?.trim();
    return url || null;
  }, [logoObjectUrl, isEditing, draft.letterheadLogoUrl, data.letterhead_logo]);

  const previewFacilityName = isEditing ? draft.name : data.facility_name;
  const previewHeader = isEditing ? draft.headerText : data.header_text ?? '';
  const previewFooter = isEditing ? draft.footerText : data.footer_text ?? '';
  */

  const handleEdit = () => {
    setDraft(facilityToFormValues(facilityData));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraft(facilityToFormValues(facilityData));
    setIsEditing(false);
  };

  const handleSave = async () => {
    const id = facilityData.id;
    if (id == null) {
      uiNotificationService?.show({
        title: 'Error',
        message: 'Facility ID is missing.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    const validation = validateFacilityDetailsViewDraft(draft);
    if (!validation.valid) {
      uiNotificationService?.show({
        title: 'Validation',
        message: 'Please fill in all required fields and correct any errors before saving.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setSaving(true);
    try {
      const payload: FacilityFormValues = {
        ...draft,
        code: facilityData.facility_code,
        status: facilityData.status ?? 'Active',
        country: draft.country?.trim() || 'India',
      };

      const response = await facilityService.updateFacility(id, payload);

      if (response.success && response.data) {
        const updated = response.data as Facility;
        const hierarchyValue =
          updated.has_radiologist_hierarchy !== undefined
            ? updated.has_radiologist_hierarchy
            : draft.hasRadiologistHierarchy === 'Yes';
        authService.syncStoredFacilityRadiologistHierarchy({
          ...updated,
          id: updated.id ?? id,
          has_radiologist_hierarchy: hierarchyValue,
        });
        uiNotificationService?.show({
          title: 'Success',
          message: response.message || 'Facility updated successfully.',
          type: 'success',
          duration: 4000,
        });
        onUpdateSuccess?.(response.data);
        setIsEditing(false);
        setDraft(facilityToFormValues(response.data as FacilityDetailsApi));
      } else {
        uiNotificationService?.show({
          title: 'Error',
          message: response.error || 'Failed to update facility.',
          type: 'error',
          duration: 6000,
        });
      }
    } catch (e) {
      console.error(e);
      uiNotificationService?.show({
        title: 'Error',
        message: 'An unexpected error occurred while saving.',
        type: 'error',
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: 'basic', label: 'Facility Details' },
    // { key: 'pacs', label: 'PACS/RIS Integration' },
    // { key: 'letterhead', label: 'Letterhead & Branding' },
  ];

  // const integrationOptions = INTEGRATION_STATUSES.map((s) => ({ value: s, label: s })); // with PACS/RIS tab
  const facilityTypeOptions = FACILITY_TYPES.map(t => ({ value: t, label: t }));
  const hierarchyOptions = [
    { value: 'Yes', label: 'Enabled' },
    { value: 'No', label: 'Disabled' },
  ];

  const renderFacilityDetailsView = () => (
    <div
      className={cn(
        'facility-details-view flex min-h-0 min-w-0 flex-col overflow-x-hidden bg-[#f9f9f9] text-[#0a0a0a] [color-scheme:light]',
        !isEditing && 'facility-details-view--readonly'
      )}
    >
      <div className="sticky top-0 z-20 shrink-0 bg-white shadow-sm">
        <div className="facility-details-header-bar">
          <div className="facility-details-header-left">
            <h1 className="facility-details-header-title">
              <span className="facility-details-header-title-label">Facility Details</span>
              <span className="facility-details-header-title-name">
                {' '}
                | {disp(isEditing ? draft.name : data.facility_name)}
              </span>
            </h1>
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-[5.2px] px-[9px] py-[3px] text-[12px] leading-[16px]',
                statusPillClass(data.status)
              )}
            >
              {statusLabel}
            </span>
          </div>
          <FacilityDetailsHeaderActions
            isEditing={isEditing}
            saving={saving}
            draftValid={draftValidation.valid}
            onCancel={handleCancel}
            onSave={handleSave}
            onEdit={handleEdit}
          />
        </div>

        {tabs.length > 1 && (
          <div className="facility-details-tabs">
            {tabs.map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'h-11 shrink-0 border-b-2 px-2 text-[14px] leading-[20px] transition-colors',
                    isActive
                      ? 'border-[#0f91b2] text-[#0f91b2]'
                      : 'border-transparent text-[#737373] hover:text-[#0a0a0a]'
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="facility-details-content space-y-6">
        {/* PACS/RIS Integration & Letterhead tabs commented out — restore tab keys + sections below when needed */}
        {activeTab === 'basic' && (
          <>
            <FacilityDetailsBasicInfoSection
              isEditing={isEditing}
              draft={draft}
              data={data}
              fieldErrors={fieldErrors}
              setField={setField}
              facilityTypeOptions={facilityTypeOptions}
              statusLabel={statusLabel}
            />
            <FacilityDetailsAddressSection
              isEditing={isEditing}
              draft={draft}
              data={data}
              fieldErrors={fieldErrors}
              setField={setField}
              stateOptions={stateOptions}
              cityOptions={cityOptions}
              isLoadingStates={isLoadingStates}
              isLoadingCities={isLoadingCities}
              stateForCityFetch={stateForCityFetch}
            />
            <FacilityDetailsReportApprovalSection
              isEditing={isEditing}
              draft={draft}
              fieldErrors={fieldErrors}
              setField={setField}
              hierarchyOptions={hierarchyOptions}
              dualStageLabel={dualStageLabel}
              seniorLabel={seniorLabel}
            />
          </>
        )}

        {/*
        {activeTab === 'pacs' && (
          <SectionCard title="PACS / RIS Integration Settings">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-[14px] font-medium text-[#0a0a0a]">PACS Configuration</p>
                <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
                  <FieldGroup label="PACS AE Title" helper="Application Entity Title for DICOM communication">
                    <EditableInput
                      id="fd-pacs-ae"
                      isEditing={isEditing}
                      value={isEditing ? draft.pacsAETitle : disp(data.pacs_ae_title)}
                      onChange={(v) => setField('pacsAETitle', v)}
                    />
                  </FieldGroup>
                  <FieldGroup label="PACS IP Address">
                    <EditableInput
                      id="fd-pacs-ip"
                      isEditing={isEditing}
                      value={isEditing ? draft.pacsIPAddress : disp(data.pacs_ip_address)}
                      onChange={(v) => setField('pacsIPAddress', v)}
                    />
                  </FieldGroup>
                  <FieldGroup label="PACS Port">
                    <EditableInput
                      id="fd-pacs-port"
                      isEditing={isEditing}
                      value={
                        isEditing
                          ? draft.pacsPort
                          : data.pacs_port != null
                            ? String(data.pacs_port)
                            : '-'
                      }
                      onChange={(v) => setField('pacsPort', v)}
                    />
                  </FieldGroup>
                  <FieldGroup label="Integration Status">
                    <EditableSelect
                      id="fd-int"
                      isEditing={isEditing}
                      value={
                        isEditing
                          ? draft.integrationStatus
                          : data.integration_status === 'Connected'
                            ? 'Connected'
                            : data.integration_status === 'Pending'
                              ? 'Pending'
                              : data.integration_status === 'Failed'
                                ? 'Failed'
                                : disp(data.integration_status)
                      }
                      onChange={(v) => setField('integrationStatus', v)}
                      options={integrationOptions}
                    />
                  </FieldGroup>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-[14px] font-medium text-[#0a0a0a]">RIS Configuration</p>
                <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
                  <FieldGroup label="RIS URL" helper="URL for Radiology Information System integration">
                    <EditableInput
                      id="fd-ris"
                      isEditing={isEditing}
                      value={isEditing ? (draft.risUrl ?? '') : disp(data.ris_url)}
                      onChange={(v) => setField('risUrl', v)}
                    />
                  </FieldGroup>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 rounded-[8px] bg-[#f3f3f5] p-4">
                <span className="text-[14px] text-[#0a0a0a]">Current Status:</span>
                <IntegrationBadge status={isEditing ? (draft.integrationStatus as IntegrationStatus) : data.integration_status} />
                <span className="text-[14px] text-[#737373]">
                  {(isEditing ? draft.integrationStatus : data.integration_status) === 'Connected' &&
                    'PACS is successfully connected and operational.'}
                  {(isEditing ? draft.integrationStatus : data.integration_status) === 'Pending' &&
                    'PACS connection is pending. Please verify configuration details.'}
                  {(isEditing ? draft.integrationStatus : data.integration_status) === 'Failed' &&
                    'PACS connection failed. Please check configuration and network connectivity.'}
                  {!isEditing && !data.integration_status && 'Integration status is not set.'}
                  {isEditing &&
                    !['Connected', 'Pending', 'Failed'].includes(draft.integrationStatus) &&
                    'Integration status is not set.'}
                </span>
              </div>
            </div>
          </SectionCard>
        )}

        {activeTab === 'letterhead' && (
          <SectionCard title="Letterhead & Branding">
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <FieldLabel>Facility Logo</FieldLabel>
                {previewLogoSrc ? (
                  <div className="w-fit rounded-[8px] border border-[#e5e5e5] bg-[#f3f3f5] p-4">
                    <img src={previewLogoSrc} alt="Facility logo" className="h-20 max-w-[200px] object-contain" />
                  </div>
                ) : (
                  <div className="flex h-20 w-[200px] items-center justify-center rounded-[8px] border border-dashed border-[#e5e5e5] bg-[#f3f3f5] text-[12px] text-[#737373]">
                    No logo
                  </div>
                )}
                {isEditing && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="text-[13px] text-[#0a0a0a] file:mr-2 file:rounded file:border file:border-[#e5e5e5] file:bg-white file:px-2 file:py-1"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setField('logo', file);
                      }}
                    />
                  </div>
                )}
                <FieldGroup label="Logo URL (optional)" helper="Use if the logo is hosted at a URL instead of uploading">
                  <EditableInput
                    id="fd-logo-url"
                    isEditing={isEditing}
                    value={isEditing ? (draft.letterheadLogoUrl ?? '') : disp(data.letterhead_logo)}
                    onChange={(v) => setField('letterheadLogoUrl', v)}
                  />
                </FieldGroup>
                <HelperText>Recommended size: 200×80 pixels (PNG or JPG)</HelperText>
              </div>

              <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
                <FieldGroup label="Header Text">
                  <EditableInput
                    id="fd-head"
                    isEditing={isEditing}
                    value={isEditing ? draft.headerText : disp(data.header_text)}
                    onChange={(v) => setField('headerText', v)}
                  />
                </FieldGroup>
                <FieldGroup label="Footer Text" helper="Appears at the bottom of generated reports">
                  <EditableInput
                    id="fd-foot"
                    isEditing={isEditing}
                    value={isEditing ? draft.footerText : disp(data.footer_text)}
                    onChange={(v) => setField('footerText', v)}
                  />
                </FieldGroup>
              </div>

              <div className="overflow-hidden rounded-[8px] border border-[#e5e5e5]">
                <div className="border-b border-[#e5e5e5] bg-[#f3f3f5] px-6 py-3">
                  <p className="text-[12px] text-[#737373]">Report Letterhead Preview</p>
                </div>
                <div className="space-y-3 p-6">
                  <div className="flex flex-wrap items-start gap-4">
                    {previewLogoSrc && <img src={previewLogoSrc} alt="" className="h-12 object-contain" />}
                    <div>
                      <p className="text-[14px] text-[#0a0a0a]">{disp(previewFacilityName)}</p>
                      {disp(previewHeader) !== '-' && (
                        <p className="text-[12px] text-[#737373]">{disp(previewHeader)}</p>
                      )}
                    </div>
                  </div>
                  {disp(previewFooter) !== '-' && (
                    <div className="border-t border-[#e5e5e5] pt-3">
                      <p className="text-[11px] text-[#737373]">{disp(previewFooter)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>
        )}
        */}
      </div>
    </div>
  );

  return renderFacilityDetailsView();
};

export default FacilityDetailsView;
